import { makeAssistantToolUI } from '@assistant-ui/react';
import { displayConversationToolSignature } from '@andsend/services/agent/shared';
import { z } from 'zod';
import { ActionCard } from '../../../action-feed/components/cards/action-feed-card';
import { useSdk } from '../../../../sdk';
import { useQuery as useTanQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { cn } from '@shadcn/ui/lib/utils';
import { Badge } from '@shadcn/ui/components/ui/badge';
import { Separator } from '@shadcn/ui/components/ui/separator';
import { CheckCircleIcon, Sparkles, UserRoundIcon } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
} from '@shadcn/ui/components/ui/dropdown-menu';
import { useActions } from '../../../../hooks/use-actions';
import { CircleCheckAnimation } from '../../../action-feed/components/cards/circle-check-animation';

type DisplayActionsToolArgs = z.infer<typeof displayConversationToolSignature.inputSchema>;
type DisplayActionsToolResult = z.infer<typeof displayConversationToolSignature.outputSchema>;

// Priority indicator component
const PRIORITY_COLORS: Record<'high' | 'medium' | 'low', string> = {
    high: 'bg-red-600',
    medium: 'bg-amber-500',
    low: 'bg-emerald-500',
};

type Priority = 'high' | 'medium' | 'low';

const PriorityIndicator: React.FC<{ priority: Priority }> = ({ priority }) => (
    <span className="flex items-center gap-1 mr-1">
        <span className={cn('inline-block w-2 h-2 rounded-full', PRIORITY_COLORS[priority])} />
    </span>
);

const ActionCardUI = ({
    args,
    result,
    status,
    toolCallId,
}: {
    args: DisplayActionsToolArgs;
    result: DisplayActionsToolResult;
    status: 'running' | 'complete' | 'incomplete' | 'requires-action';
    toolCallId: string;
}) => {
    const {
        prospect: { findProspectData },
    } = useSdk();
    const { prospectIdsWithActions, isLoadingActions, totalActionsCount, prospectIdsWithCompletedActions } =
        useActions();
    const cardRef = useRef<HTMLDivElement>(null);
    const toolUIRef = useRef<HTMLDivElement>(null);
    const threadId = useParams().threadId;
    const [expanded, setExpanded] = useState(false);
    const [activeProspectId, setActiveProspectId] = useState<string | null>(null);
    const [badgeProspectIds, setBadgeProspectIds] = useState<string[]>([]);
    const [dropdownProspectIds, setDropdownProspectIds] = useState<string[]>([]);
    const [prospectIdsCompletedThisSession, setProspectIdsCompletedThisSession] = useState<string[]>([]);
    const [prospectIdsNotCompletedThisSession, setProspectIdsNotCompletedThisSession] = useState<string[]>([]);
    const hasInitializedProspectIdsNotCompletedThisSession = useRef(false);
    const prevCompletedMapRef = useRef<Record<string, boolean>>({});

    const prospectIds = useMemo(() => {
        if (!args.prospectIds || !args.actions) return args.prospectIds;

        // Create a priority map for faster lookup
        const priorityMap: Record<string, Priority> = {};
        args.actions.forEach((a) => {
            priorityMap[a.prospectId] = a.priority;
        });

        // Sort by priority: high -> medium -> low
        return [...args.prospectIds].sort((a, b) => {
            const priorityA = priorityMap[a] || 'low';
            const priorityB = priorityMap[b] || 'low';

            const priorityOrder: Record<Priority, number> = { high: 0, medium: 1, low: 2 };
            return priorityOrder[priorityA] - priorityOrder[priorityB];
        });
    }, [args.prospectIds, args.actions]);

    // --- LocalStorage persistence logic ---
    const LOCAL_STORAGE_KEY = `display-actions-tool-ui-state-${toolCallId}`;

    // Hydrate state from localStorage on mount
    useEffect(() => {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (parsed) {
                    setExpanded(parsed.expanded ?? false);
                    setActiveProspectId(parsed.activeProspectId ?? null);
                    setBadgeProspectIds(parsed.badgeProspectIds ?? []);
                    setDropdownProspectIds(parsed.dropdownProspectIds ?? []);
                    setProspectIdsCompletedThisSession(parsed.prospectIdsCompletedThisSession ?? []);
                    setProspectIdsNotCompletedThisSession(parsed.prospectIdsNotCompletedThisSession ?? []);
                }
            } catch {}
        }
    }, [LOCAL_STORAGE_KEY]);

    // Persist state to localStorage on change
    useEffect(() => {
        localStorage.setItem(
            LOCAL_STORAGE_KEY,
            JSON.stringify({
                expanded,
                activeProspectId,
                badgeProspectIds,
                dropdownProspectIds,
                prospectIdsCompletedThisSession,
                prospectIdsNotCompletedThisSession,
            })
        );
    }, [
        expanded,
        activeProspectId,
        badgeProspectIds,
        dropdownProspectIds,
        prospectIdsCompletedThisSession,
        prospectIdsNotCompletedThisSession,
        LOCAL_STORAGE_KEY,
    ]);
    // --- End LocalStorage persistence logic ---

    const completedProspects = useMemo(() => {
        return new Set(prospectIdsWithCompletedActions);
    }, [prospectIdsWithCompletedActions]);

    const { data: prospectData, isLoading: isProspectLoading } = useTanQuery(
        findProspectData(
            {
                prospectIds: prospectIds,
                page: 1,
                perPage: 50,
                includeFields: [
                    'fullName',
                    'linkedinProfileImgUrl',
                    'occupationTitle',
                    'organizationName',
                    'linkedinUserId',
                    'organizationId',
                ],
            },
            {
                enabled: !!args.prospectIds,
            }
        )
    );
    console.log('newProspectData', prospectData);

    useEffect(() => {
        if (prospectData?.prospects?.length) {
            // Only set if not already set (preserve localStorage state)
            if (badgeProspectIds.length === 0 && dropdownProspectIds.length === 0) {
                const ids = prospectIds;
                setBadgeProspectIds(ids.slice(0, 3));
                setDropdownProspectIds(ids.slice(3));
                if (!activeProspectId) {
                    setActiveProspectId(ids[0]);
                }
            }
        }
    }, [prospectData]);

    useEffect(() => {
        if (prospectIdsWithActions.length > 0 && !hasInitializedProspectIdsNotCompletedThisSession.current) {
            setProspectIdsNotCompletedThisSession((prev) => {
                const newIds = prospectIdsWithActions.filter((id) => !completedProspects.has(id));
                return [...prev, ...newIds];
            });
            hasInitializedProspectIdsNotCompletedThisSession.current = true;
        }
    }, [prospectIdsWithActions]);

    // On mount, initialize prevCompletedMapRef to current completions
    useEffect(() => {
        const initialCompletedMap: Record<string, boolean> = {};
        prospectIds?.forEach((prospectId) => {
            initialCompletedMap[prospectId] =
                completedProspects.has(prospectId) && !prospectIdsWithActions.includes(prospectId);
        });
        prevCompletedMapRef.current = initialCompletedMap;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    useEffect(() => {
        // Build a map of current completions for all prospectIds
        const currentCompletedMap: Record<string, boolean> = {};
        prospectIds?.forEach((prospectId) => {
            currentCompletedMap[prospectId] =
                completedProspects.has(prospectId) && !prospectIdsWithActions.includes(prospectId);
        });

        // Compare with previous completions
        const prevCompletedMap = prevCompletedMapRef.current;
        const newlyCompleted: string[] = [];
        prospectIds?.forEach((prospectId) => {
            if (
                !prevCompletedMap[prospectId] && // was not completed before
                currentCompletedMap[prospectId] // is completed now
            ) {
                newlyCompleted.push(prospectId);
            }
        });

        // Update session state if any new completions
        if (newlyCompleted.length > 0) {
            setProspectIdsCompletedThisSession((prev) => [
                ...prev,
                ...newlyCompleted.filter((id) => !prev.includes(id)),
            ]);
        }

        // Update the ref for next comparison
        prevCompletedMapRef.current = currentCompletedMap;
    }, [prospectIds, completedProspects, prospectIdsWithActions]);

    const handleDropdownSelect = (prospectId: string) => {
        // Move selected dropdown prospect to third badge, move old third badge to dropdown
        if (badgeProspectIds.length < 3) return;
        const newBadges = [...badgeProspectIds];
        const newDropdown = [...dropdownProspectIds];
        const oldThird = newBadges[2];
        newBadges[2] = prospectId;
        const idx = newDropdown.indexOf(prospectId);
        if (idx !== -1) {
            newDropdown.splice(idx, 1, oldThird);
        }
        setBadgeProspectIds(newBadges);
        setDropdownProspectIds(newDropdown);
        setActiveProspectId(prospectId);
    };

    useEffect(() => {
        if (!expanded) return;

        function handleClickOutside(event: MouseEvent) {
            if (toolUIRef.current) {
                const rect = toolUIRef.current.getBoundingClientRect();
                const clickX = event.clientX;
                const clickY = event.clientY;

                if (
                    clickX < rect.left - 140 ||
                    clickX > rect.right + 140 ||
                    clickY < rect.top ||
                    clickY > rect.bottom
                ) {
                    setExpanded(false);
                }
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [expanded]);

    if (prospectIds === undefined) return null;

    if (status === 'running') {
        return <p>Loading...</p>;
    }

    if (status === 'complete') {
        return (
            <div className={cn('w-[calc(var(--thread-max-width)*0.85)] flex flex-col gap-4 mt-4 py-4')} ref={toolUIRef}>
                <div className="flex flex-row gap-2 *:gap-1 *:text-sm">
                    {badgeProspectIds?.length > 1 &&
                        badgeProspectIds.map((prospectId) => {
                            const isCompleted = prospectIdsCompletedThisSession.includes(prospectId);
                            const variant = isCompleted
                                ? 'success'
                                : activeProspectId === prospectId
                                ? 'brand'
                                : 'outline';
                            // Get priority for this prospectId
                            const contactMotivation = args.actions?.find((a) => a.prospectId === prospectId);
                            const priority = contactMotivation?.priority ?? 'low';
                            return (
                                <Badge
                                    variant={variant}
                                    key={prospectId}
                                    className={cn(
                                        'cursor-pointer',
                                        !(variant === 'success' && activeProspectId === prospectId) &&
                                            'bg-background-primary'
                                    )}
                                    onClick={() => {
                                        setActiveProspectId(prospectId);
                                    }}
                                >
                                    <PriorityIndicator priority={priority} />
                                    {/* {variant === 'success' ? (
                                        <CircleCheckAnimation className={cn('w-3 h-3')} />
                                    ) : (
                                        <UserRoundIcon className={cn('w-3 h-3')} />
                                    )} */}
                                    <span>
                                        {prospectData?.prospects?.find((p) => p.prospectId === prospectId)?.fullName}
                                    </span>
                                </Badge>
                            );
                        })}
                    {dropdownProspectIds.length > 0 && (
                        <DropdownMenu>
                            <DropdownMenuTrigger>
                                <Badge variant="outline" className="cursor-pointer bg-background-primary text-sm">
                                    +{dropdownProspectIds.length} more
                                </Badge>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                {dropdownProspectIds.map((prospectId) => {
                                    const isCompleted = prospectIdsCompletedThisSession.includes(prospectId);
                                    // Get priority for this prospectId
                                    const contactMotivation = args.actions?.find((a) => a.prospectId === prospectId);
                                    const priority = contactMotivation?.priority ?? 'low';
                                    return (
                                        <DropdownMenuItem
                                            key={prospectId}
                                            className={cn(`${isCompleted ? 'bg-success-50' : ''}`)}
                                            onClick={() => handleDropdownSelect(prospectId)}
                                        >
                                            <PriorityIndicator priority={priority} />
                                            {isCompleted ? (
                                                <CircleCheckAnimation className={cn('w-3 h-3')} />
                                            ) : (
                                                <UserRoundIcon className={cn('w-3 h-3')} />
                                            )}
                                            {
                                                prospectData?.prospects?.find((p) => p.prospectId === prospectId)
                                                    ?.fullName
                                            }
                                        </DropdownMenuItem>
                                    );
                                })}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
                {prospectData?.prospects?.find((p) => p.prospectId === activeProspectId) && (
                    <ActionCard
                        prospectId={activeProspectId}
                        prospectData={prospectData?.prospects?.find((p) => p.prospectId === activeProspectId)}
                        baseUrl={`/new/home/${threadId}`}
                        ref={cardRef}
                        isExpanded={expanded}
                        className="max-h-[min(83dvh,1000px)]"
                        isCompleted={
                            completedProspects.has(activeProspectId) &&
                            !prospectIdsWithActions.includes(activeProspectId)
                        }
                        onClick={() => {
                            setExpanded(!expanded);
                            if (cardRef.current) {
                                setTimeout(() => {
                                    cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }, 300);
                            }
                        }}
                        overrideMotivation={args.actions?.find((a) => a.prospectId === activeProspectId)?.reason}
                        overrideMessage={args.actions?.find((a) => a.prospectId === activeProspectId)?.message}
                        hideScheduling
                    />
                )}
            </div>
        );
    }

    return <div>Error</div>;
};

export const DisplayActionsToolUI = makeAssistantToolUI<DisplayActionsToolArgs, DisplayActionsToolResult>({
    toolName: displayConversationToolSignature.name,
    render: ({ args, result, status, toolCallId }) => (
        <ActionCardUI args={args} result={result} status={status.type} toolCallId={toolCallId} key={toolCallId} />
    ),
});
